import { watch } from 'vue'
import { tryUseNuxtApp } from '#imports'
import { hubState } from '~/lib/hubState'
import { fetchMe, startMessaging, stopMessaging, unreadOrderIds, unreadThreadCount, useMessaging } from '#hub-messaging/composables/useMessaging'

// The shop's messages in Johku Hub, the messaging service, with Hub's own components (the
// #hub-messaging layer: HubInbox in components/HubMessages.vue, HubOrderConversation in
// components/HubOrderPane.vue). The POS server fetches a token for the signed-in user
// (/<shop>/hub-token) that only reaches this shop's conversations; with it the POS keeps the
// shop's conversations on the device, synced and in real time, for the menu's unread count, the
// Messages view and the order view's Conversation tab.

// Hub couldn't be reached: try again in a minute
const RETRY_MS = 60 * 1000
// Not in the shop's inbox, or signed in without a Johku account: ask again much later.
const NO_ACCESS_RETRY_MS = 15 * 60 * 1000

export { hubState }

let current = null
let pending = null
let session = null
let starting = null
let retryTimer = null
let unwatch = null

// Never throws: after an await on the server there is no Nuxt instance to read it from (NUXT_E1001)
export function hubUrl () {
  return String(tryUseNuxtApp()?.$config.public.hubUrl || '').replace(/\/$/, '')
}

// A token for the shop, reused until it is close to running out. Rejects with the POS
// server's error code (HUB_NOT_A_MEMBER, HUB_NEEDS_JOHKU_LOGIN, HUB_UNAVAILABLE, …).
export function hubToken (shopId, { renew = false } = {}) {
  const now = Date.now() / 1000
  if (!renew && current && current.shopId === shopId && current.expiresAt - now > 60) {
    return Promise.resolve(current)
  }
  if (pending) {
    return pending
  }
  pending = $fetch('/' + shopId + '/hub-token', { method: 'POST', body: { renew } })
    .then((token) => {
      current = { ...token, shopId }
      hubState.available = true
      return current
    })
    .catch((error) => {
      const { code, status } = errorCode(error)
      if (status === 401 || status === 403 || status === 404) {
        current = null
        hubState.available = false
        hubState.unread = 0
        hubState.unreadOrders = []
      }
      throw Object.assign(new Error(code), { code, status })
    })
    .finally(() => {
      pending = null
    })
  return pending
}

function errorCode (error) {
  const status = error?.response?.status
  return { code: error?.data?.data?.code || error?.data?.code || (status === 401 ? 'NOT_SIGNED_IN' : 'HUB_UNAVAILABLE'), status }
}

// Stores an order's details in Hub ({ identifier, locale, orderUrl }), so its conversation
// can open. Resolves with Hub's order id and thread id (null before the first message).
export function prepareHubOrder (shopId, order) {
  return $fetch('/' + shopId + '/hub-order', { method: 'POST', body: order })
    .catch((error) => {
      const { code, status } = errorCode(error)
      throw Object.assign(new Error(code), { code, status })
    })
}

// What to tell the user when the messages can't be shown
export function hubProblemText (code, _) {
  switch (code) {
    case 'HUB_NOT_A_MEMBER':
      return _("Your user account doesn't have this shop's messages.")
    case 'HUB_NEEDS_JOHKU_LOGIN':
      return _('Log in with your Johku account to see messages.')
    case 'NOT_SIGNED_IN':
      return _('Log in to see messages.')
    default:
      return _("Messages can't be reached right now.")
  }
}

// Opens the shop's conversations for the signed-in user: once, and again for another user.
// Resolves true when they're open. Does nothing when the POS has no Hub address, nor on the
// server: the messages live in the browser, and this module's state would be shared by every
// request the server renders.
export function startHubMessaging (shopId) {
  if (false || !hubUrl()) {
    return Promise.resolve(false)
  }
  if (!starting) {
    starting = open(shopId).finally(() => {
      starting = null
    })
  }
  return starting
}

async function open (shopId) {
  clearTimeout(retryTimer)
  let token
  try {
    token = await hubToken(shopId)
  } catch (error) {
    hubState.problem = error.code
    // 404: this POS has no messages at all
    if (error.status !== 404) {
      retryLater(shopId, error.status === 403 ? NO_ACCESS_RETRY_MS : RETRY_MS)
    }
    return false
  }
  if (session && session.shopId === shopId && session.personId === token.personId && useMessaging().status === 'ready') {
    return true
  }
  let me
  try {
    me = await fetchMe(hubUrl(), token.token)
  } catch (error) {
    const noAccess = error?.status === 403
    hubState.problem = noAccess ? 'HUB_NOT_A_MEMBER' : 'HUB_UNAVAILABLE'
    retryLater(shopId, noAccess ? NO_ACCESS_RETRY_MS : RETRY_MS)
    return false
  }
  await startMessaging({
    apiBase: hubUrl(),
    token: token.token,
    scope: 'shop:' + shopId,
    // A till is shared: only the person signed in now keeps a copy on it
    sharedDevice: true,
    renewToken: () => hubToken(shopId, { renew: true }).then((t) => t.token).catch(() => null),
    onEnded: () => {
      session = null
      current = null
      hubState.available = false
      hubState.problem = 'HUB_NOT_A_MEMBER'
      retryLater(shopId, NO_ACCESS_RETRY_MS)
    }
  }, me)
  session = { shopId, personId: me.person.id }
  hubState.problem = ''
  hubState.available = true
  watchUnread()
  return true
}

function retryLater (shopId, ms) {
  clearTimeout(retryTimer)
  retryTimer = setTimeout(() => startHubMessaging(shopId), ms)
}

// The menu's count and the order tab's badge follow the conversations as they sync
function watchUnread () {
  if (unwatch) {
    return
  }
  const messaging = useMessaging()
  unwatch = watch(() => [messaging.status, messaging.threads], () => {
    const ready = messaging.status === 'ready'
    hubState.unread = ready ? unreadThreadCount() : 0
    hubState.unreadOrders = ready ? unreadOrderIds() : []
  }, { immediate: true, deep: true })
}

// Starts the shop's messages for the menu. Kept under its old name for pages/[shopid]/index.vue.
export function startHubUnread (shopId) {
  return startHubMessaging(shopId)
}

// Signed in again (another user, or after a Johku login): start over for whoever is signed in now.
export function recheckHub (shopId) {
  stopHubUnread()
  current = null
  startHubMessaging(shopId)
}

export function stopHubUnread () {
  clearTimeout(retryTimer)
  stopMessaging()
  session = null
  hubState.unread = 0
  hubState.unreadOrders = []
}
